import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useWarehouseLocal } from '@/hooks/useWarehouseLocal';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { ProductOut, DrinkTypeOut, FoodTypeOut, EventOut, BulkImportResult as BulkImportResultData, CountryTaxRatesResponse } from '@/types/api';
import { isOrganizer, isSuperAdmin } from '@/utils/roleUtils';
import BulkImportResult from '@/components/molecules/BulkImportResult/BulkImportResult';
import ProductCartaPanel from './ProductCartaPanel';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import Tab from '@/components/atoms/Tab';
import Tabs from '@/components/atoms/Tabs';
import { FormField } from '@/components/molecules/FormField';
import FileUpload from '@/components/atoms/FileUpload/FileUpload';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import './Products.scss';

interface FormState {
  name: string;
  description: string;
  image_url: string | null;
  purchase_price: number | '';
  is_drink: boolean;
  type_id: number | '';
}

const emptyForm: FormState = {
  name: '',
  description: '',
  image_url: null,
  purchase_price: '',
  is_drink: true,
  type_id: '',
};

type StockLevel = 'ok' | 'low' | 'hidden';

// Same rules as the warehouse Stock page: hidden from customers at or below
// hide_threshold, low-stock alert at or below min_threshold.
const getStockLevel = (product: ProductOut): StockLevel => {
  const stock = product.stock;
  if (!stock) return 'ok';
  if (stock.quantity <= stock.hide_threshold) return 'hidden';
  if (stock.quantity <= stock.min_threshold) return 'low';
  return 'ok';
};

const Products: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { localId, setLocalId, availableLocals, loading: localLoading } = useWarehouseLocal();
  const canEditCarta = isOrganizer(user) || isSuperAdmin(user);

  const [products, setProducts] = useState<ProductOut[]>([]);
  const [drinkTypes, setDrinkTypes] = useState<DrinkTypeOut[]>([]);
  const [foodTypes, setFoodTypes] = useState<FoodTypeOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'drinks' | 'foods'>(
    searchParams.get('tab') === 'foods' ? 'foods' : 'drinks'
  );

  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportFile, setBulkImportFile] = useState<File | null>(null);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState<BulkImportResultData | null>(null);

  const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
  const [localEvents, setLocalEvents] = useState<EventOut[]>([]);
  const [countryTaxRates, setCountryTaxRates] = useState<CountryTaxRatesResponse | null>(null);

  // ?local=<id> (e.g. from the carta's "create products" link) preselects
  // that Local once the user's Locals are known.
  useEffect(() => {
    const requested = parseInt(searchParams.get('local') || '');
    if (requested && availableLocals.some((l) => l.id === requested)) {
      setLocalId(requested);
    }
  }, [availableLocals, searchParams, setLocalId]);

  // Events of the selected Local, for "Añadir a carta"
  useEffect(() => {
    if (!localId || !canEditCarta) {
      setLocalEvents([]);
      return;
    }
    const load = isSuperAdmin(user)
      ? apiService.getEvents()
      : apiService.getMyManagedEvents();
    load
      .then((events) => setLocalEvents(events.filter((e) => e.local_id === localId)))
      .catch(() => setLocalEvents([]));
  }, [localId, canEditCarta, user]);

  useEffect(() => {
    if (!canEditCarta) return;
    apiService.getAllCountryTaxRates()
      .then((res) => setCountryTaxRates(res))
      .catch(() => {});
  }, [canEditCarta]);

  useEffect(() => {
    apiService.getDrinkTypes().then(setDrinkTypes).catch(() => {});
    apiService.getFoodTypes().then(setFoodTypes).catch(() => {});
  }, []);

  const loadProducts = useCallback(async () => {
    if (!localId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiService.getProducts(localId);
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError(t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [localId, t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const openCreateForm = () => {
    setEditingId(null);
    setForm({ ...emptyForm, is_drink: activeTab === 'drinks' });
    setImageFile(null);
    setShowForm(true);
  };

  const openEditForm = (product: ProductOut) => {
    setEditingId(product.id);
    setForm({
      name: product.name,
      description: product.description || '',
      image_url: product.image_url || null,
      purchase_price: product.purchase_price,
      is_drink: product.is_drink,
      type_id: product.type_id ?? '',
    });
    setImageFile(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    setImageFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!localId || !form.name) return;
    if (form.type_id === '') {
      setError(t('products.typeRequired'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      let imageUrl = form.image_url;
      if (imageFile) {
        const uploaded = await apiService.uploadImage(imageFile);
        imageUrl = uploaded.url;
      }

      if (editingId) {
        const updated = await apiService.updateProduct(editingId, {
          name: form.name,
          description: form.description || null,
          image_url: imageUrl,
          purchase_price: form.purchase_price === '' ? 0 : Number(form.purchase_price),
          type_id: Number(form.type_id),
        });
        setProducts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      } else {
        const created = await apiService.createProduct({
          local_id: localId,
          name: form.name,
          description: form.description || null,
          image_url: imageUrl,
          purchase_price: form.purchase_price === '' ? 0 : Number(form.purchase_price),
          is_drink: form.is_drink,
          type_id: Number(form.type_id),
        });
        setProducts((prev) => [...prev, created]);
      }
      closeForm();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setSubmitting(false);
    }
  };

  const isDrinkTab = activeTab === 'drinks';

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadProductsTemplate(isDrinkTab);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = isDrinkTab ? 'drink_products_template.xlsx' : 'food_products_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    }
  };

  const handleBulkImport = async () => {
    if (!localId || !bulkImportFile) return;
    setIsBulkImporting(true);
    setError(null);
    try {
      const result = await apiService.bulkImportProducts(bulkImportFile, localId, isDrinkTab);
      setBulkImportResult(result);
      setBulkImportFile(null);
      await loadProducts();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setIsBulkImporting(false);
    }
  };

  const closeBulkImport = () => {
    setShowBulkImport(false);
    setBulkImportFile(null);
    setBulkImportResult(null);
  };

  const handleDelete = async (product: ProductOut) => {
    if (!window.confirm(t('products.confirmDelete') as string)) return;
    setError(null);
    try {
      await apiService.deleteProduct(product.id);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    }
  };

  const types = form.is_drink ? drinkTypes : foodTypes;
  const filteredProducts = products.filter((p) => p.is_drink === (activeTab === 'drinks'));

  if (!localLoading && !localId) {
    return (
      <div className="products-page">
        <Card variant="elevated">
          <p>{t('warehouse.dashboard.noLocalAssigned')}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-page__header">
        <h2 className="products-page__title">{t('products.title')}</h2>
        {availableLocals.length > 0 && (
          <SearchableSelect<number>
            className="products-page__local-select"
            options={availableLocals.map((local) => ({ value: local.id, label: local.name }))}
            value={localId}
            onChange={(value) => setLocalId(value)}
          />
        )}
        <div className="products-page__header-actions">
          <Button variant="outline" size="sm" onClick={() => setShowBulkImport(true)} disabled={showBulkImport}>
            {t('products.bulkImport.open')}
          </Button>
          <Button variant="primary" size="sm" onClick={openCreateForm} disabled={showForm}>
            {t('products.newProduct')}
          </Button>
        </div>
      </div>

      <p className="products-page__intro">{t('products.intro')}</p>

      {error && <div className="products-page__error">{error}</div>}

      {showForm && (
        <Card variant="elevated" className="products-page__form-card">
          <div className="products-page__form-header">
            <h3>{editingId ? t('products.editProduct') : t('products.newProduct')}</h3>
            <Button type="button" variant="outline" size="sm" onClick={closeForm}>
              {t('common.cancel')}
            </Button>
          </div>
          <form onSubmit={handleSubmit} className="products-page__form">
            <FormField
              type="text"
              name="name"
              label={t('products.name')}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              disabled={submitting}
              required
            />
            <FormField
              type="textarea"
              name="description"
              label={t('products.description')}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              disabled={submitting}
            />
            <FileUpload
              name="image"
              label={t('products.photo')}
              value={form.image_url}
              onChange={(file) => setImageFile(file)}
              accept="image/*"
              maxSize={5}
              disabled={submitting}
            />
            <FormField
              type="number"
              name="purchase_price"
              label={t('products.purchasePrice')}
              value={form.purchase_price}
              onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value ? Number(e.target.value) : '' }))}
              disabled={submitting}
              min="0"
              step="0.01"
            />

            {!editingId && (
              <div className="products-page__type-toggle">
                <Tabs>
                  <Tab active={form.is_drink} onClick={() => setForm((f) => ({ ...f, is_drink: true, type_id: '' }))}>
                    {t('warehouse.addProductModal.drink')}
                  </Tab>
                  <Tab active={!form.is_drink} onClick={() => setForm((f) => ({ ...f, is_drink: false, type_id: '' }))}>
                    {t('warehouse.addProductModal.food')}
                  </Tab>
                </Tabs>
              </div>
            )}

            <SearchableSelect<number>
              name="type_id"
              label={t('warehouse.addProductModal.type')}
              placeholder="—"
              required
              options={types.map((tp) => ({ value: tp.id, label: tp.name }))}
              value={form.type_id === '' ? null : form.type_id}
              onChange={(value) => setForm((f) => ({ ...f, type_id: value ?? '' }))}
              disabled={submitting}
            />

            <div className="products-page__form-actions">
              <Button type="button" variant="outline" onClick={closeForm} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={submitting} loading={submitting}>
                {t('common.save')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {showBulkImport && (
        <Card variant="elevated" className="products-page__form-card">
          <div className="products-page__form-header">
            <h3>
              {t(isDrinkTab ? 'products.bulkImport.titleDrinks' : 'products.bulkImport.titleFoods')}
            </h3>
            <Button type="button" variant="ghost" size="sm" onClick={closeBulkImport}>
              {t('common.close')}
            </Button>
          </div>
          <p className="products-page__bulk-import-description">
            {t('products.bulkImport.description', {
              local: availableLocals.find((l) => l.id === localId)?.name ?? '',
            })}
          </p>
          <div className="products-page__bulk-import-row">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => {
                setBulkImportFile(e.target.files?.[0] ?? null);
                setBulkImportResult(null);
              }}
              disabled={isBulkImporting}
            />
          </div>
          <div className="products-page__form-actions">
            <Button type="button" variant="ghost" size="sm" onClick={handleDownloadTemplate}>
              {t('products.bulkImport.downloadTemplate')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleBulkImport}
              disabled={!bulkImportFile || isBulkImporting || !localId}
              loading={isBulkImporting}
            >
              {t('products.bulkImport.import')}
            </Button>
          </div>
          {bulkImportResult && (
            <BulkImportResult result={bulkImportResult} onDismiss={() => setBulkImportResult(null)} />
          )}
        </Card>
      )}

      <div className="products-page__tabs">
        <Tabs>
          <Tab active={activeTab === 'drinks'} onClick={() => { setActiveTab('drinks'); setBulkImportResult(null); }}>
            {t('warehouse.addProductModal.drink')} ({products.filter((p) => p.is_drink).length})
          </Tab>
          <Tab active={activeTab === 'foods'} onClick={() => { setActiveTab('foods'); setBulkImportResult(null); }}>
            {t('warehouse.addProductModal.food')} ({products.filter((p) => !p.is_drink).length})
          </Tab>
        </Tabs>
      </div>

      {!loading && filteredProducts.length === 0 ? (
        <Card variant="elevated">
          <p>{t('products.empty')}</p>
        </Card>
      ) : (
        <div className="products-page__list">
          {filteredProducts.map((product) => {
            const level = getStockLevel(product);
            const expanded = expandedProductId === product.id;
            return (
              <Card key={product.id} variant="elevated" className="products-page__item-card">
                <div className="products-page__item">
                  {product.image_url && (
                    <img className="products-page__item-image" src={product.image_url} alt={product.name} />
                  )}
                  <div className="products-page__item-info">
                    <h4>{product.name}</h4>
                    {product.description && <p>{product.description}</p>}
                    <div className="products-page__item-meta">
                      <span className="products-page__item-stock">
                        {t('products.stock')}: <strong>{product.stock?.quantity ?? 0}</strong>
                      </span>
                      {level !== 'ok' && (
                        <span className={`products-page__stock-badge products-page__stock-badge--${level}`}>
                          {t(level === 'hidden' ? 'products.stockHidden' : 'products.stockLow')}
                        </span>
                      )}
                      <span className="products-page__item-price">
                        {t('products.purchasePrice')}: {product.purchase_price}
                      </span>
                    </div>
                  </div>
                  <div className="products-page__item-actions">
                    <Button
                      variant={expanded ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => setExpandedProductId(expanded ? null : product.id)}
                    >
                      {t(canEditCarta ? 'products.carta.toggle' : 'products.carta.inCartas')}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEditForm(product)}>
                      {t('common.edit')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(product)}>
                      {t('common.delete')}
                    </Button>
                  </div>
                </div>
                {expanded && (
                  <ProductCartaPanel
                    product={product}
                    events={localEvents}
                    countryTaxRates={(product.is_drink ? countryTaxRates?.country_drink_tax_rates : countryTaxRates?.country_tax_rates) || {}}
                    canAddToCarta={canEditCarta}
                  />
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Products;
